import {useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {
    useBallotList,
    useCreateBallot,
    useDeleteBallot,
    useDuplicateBallot,
} from '../../shared/api/useBallots';
import {useCurrentUser} from '../../shared/api/useCurrentUser';
import {ConfirmDialog} from '../../shared/components/confirmDialog';
import {ErrorNotice} from '../../shared/components/notice';
import {Votes} from './votes';

export const VotesContainer = () => {
    const navigate = useNavigate();
    const {data: user} = useCurrentUser();
    const canSeeAll = user?.role === 'site_admin';
    const [all, setAll] = useState(false);
    const [deleting, setDeleting] = useState<string | null>(null);
    const list = useBallotList(canSeeAll && all);
    const create = useCreateBallot();
    const duplicate = useDuplicateBallot();
    const remove = useDeleteBallot();

    const state = list.isPending ? 'loading' : list.isError ? 'error' : 'ready';
    const busyId = duplicate.isPending ? (duplicate.variables ?? null) : null;

    const failure = create.error ?? duplicate.error;
    const failureTitle = create.error
        ? 'Could not create the vote'
        : 'Could not duplicate the vote';

    return (
        <>
            {failure && <ErrorNotice title={failureTitle} error={failure} />}
            <Votes
                state={state}
                error={list.error}
                ballots={list.data ?? []}
                canSeeAll={canSeeAll}
                all={all}
                creating={create.isPending}
                busyId={busyId}
                onAllChange={setAll}
                onNew={() =>
                    create.mutate(undefined, {
                        onSuccess: (ballot) => navigate(`/admin/ballots/${ballot.id}`),
                    })
                }
                onPresent={(id) => window.open(`/present/${id}`, '_blank')}
                onDuplicate={(id) =>
                    duplicate.mutate(id, {
                        onSuccess: (copy) => navigate(`/admin/ballots/${copy.id}`),
                    })
                }
                onDelete={setDeleting}
            />
            <ConfirmDialog
                open={deleting !== null}
                title="Delete this vote? Its results go too."
                body="This cannot be undone."
                confirmLabel="Delete vote"
                busy={remove.isPending}
                error={remove.error}
                onCancel={() => {
                    remove.reset();
                    setDeleting(null);
                }}
                onConfirm={() => {
                    if (deleting) remove.mutate(deleting, {onSuccess: () => setDeleting(null)});
                }}
            />
        </>
    );
};
